import React, { useState } from 'react';
import { Bell, Circle } from 'lucide-react';

const initials = (name) => (name || '?').trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

const timeAgo = (isoString) => {
  if (!isoString) return '';
  const seconds = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const AdminAvatar = ({ admin }) => (
  <div className="relative shrink-0" title={`${admin.fullName} — ${admin.status}`}>
    <div className="h-8 w-8 rounded-full bg-paper/20 text-paper text-xs font-semibold flex items-center justify-center border border-paper/30">
      {initials(admin.fullName || admin.username)}
    </div>
    <Circle
      className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-ink ${
        admin.status === 'online' ? 'text-green-500 fill-green-500' : 'text-yellow-500 fill-yellow-500'
      }`}
    />
  </div>
);

/** WhatsApp-style "who's online" + a live feed of recent catalog changes, for the dashboard topbar. */
const PresenceBar = ({ onlineAdmins, myOtherSessions = [], recentUpdates }) => {
  const [openPanel, setOpenPanel] = useState(null); // null | 'presence' | 'updates'

  return (
    <div className="flex items-center gap-2">
      {/* Recent updates bell */}
      <div className="relative">
        <button
          onClick={() => setOpenPanel((p) => (p === 'updates' ? null : 'updates'))}
          className="relative p-2.5 text-paper/80 hover:text-paper hover:bg-paper/10 rounded-full transition-colors"
          aria-label="Recent updates"
        >
          <Bell className="h-4.5 w-4.5" />
          {recentUpdates.length > 0 && (
            <span className="absolute top-1 right-1 h-2 w-2 bg-accent rounded-full" />
          )}
        </button>
        {openPanel === 'updates' && (
          <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-surface rounded-md shadow-2xl border border-line z-50">
            <div className="px-4 py-3 border-b border-line font-display text-ink">Recent updates</div>
            {recentUpdates.length === 0 ? (
              <p className="px-4 py-6 text-sm text-ink-faint text-center">Nothing yet — changes made by any admin will show up here.</p>
            ) : (
              recentUpdates.map((u, i) => (
                <div key={`${u.id}-${u.timestamp}-${i}`} className="px-4 py-3 border-b border-line last:border-0 text-sm">
                  <p className="text-ink">
                    <span className="font-medium">{u.adminName || 'Someone'}</span>{' '}
                    {{ created: 'added', updated: 'updated', deleted: 'removed' }[u.action] || u.action}{' '}
                    <span className="font-medium">"{u.name}"</span>
                  </p>
                  <p className="text-ink-faint text-xs mt-0.5">{timeAgo(u.timestamp)}</p>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Online admins */}
      <div className="relative">
        <button
          onClick={() => setOpenPanel((p) => (p === 'presence' ? null : 'presence'))}
          className="flex items-center -space-x-2 p-1 rounded-full hover:bg-paper/10 transition-colors"
          aria-label="Online admins"
        >
          {onlineAdmins.slice(0, 4).map((admin) => (
            <AdminAvatar key={admin.sessionId} admin={admin} />
          ))}
          {onlineAdmins.length === 0 && myOtherSessions.length === 0 && (
            <span className="text-xs text-paper/50 px-2">No one else online</span>
          )}
          {myOtherSessions.length > 0 && (
            <span className="ml-2 flex items-center gap-1 text-xs text-paper/70 px-2 py-1 rounded-full bg-paper/10">
              <Circle className="h-2 w-2 text-green-500 fill-green-500" />
              Also you: {myOtherSessions.length} device{myOtherSessions.length > 1 ? 's' : ''}
            </span>
          )}
        </button>
        {openPanel === 'presence' && (onlineAdmins.length > 0 || myOtherSessions.length > 0) && (
          <div className="absolute right-0 mt-2 w-72 max-h-96 overflow-y-auto bg-surface rounded-md shadow-2xl border border-line z-50">
            {myOtherSessions.length > 0 && (
              <>
                <div className="px-4 py-3 border-b border-line font-display text-ink">Also signed in as you</div>
                {myOtherSessions.map((session) => (
                  <div key={session.sessionId} className="px-4 py-3 border-b border-line flex items-start gap-3 bg-accent-soft/30">
                    <div className="h-8 w-8 rounded-full bg-accent/20 text-accent-deep text-xs font-semibold flex items-center justify-center shrink-0">
                      {initials(session.fullName || session.username)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-ink font-medium truncate">{session.device}</p>
                      <p className="text-xs text-ink-faint">
                        <span className={session.status === 'online' ? 'text-green-600' : 'text-yellow-600'}>
                          {session.status === 'online' ? 'Online' : 'Away'}
                        </span>
                        {session.currentPage && ` · on ${session.currentPage}`}
                        {(session.city || session.country) && ` · ${[session.city, session.country].filter(Boolean).join(', ')}`}
                      </p>
                    </div>
                  </div>
                ))}
              </>
            )}
            {onlineAdmins.length > 0 && (
              <>
                <div className="px-4 py-3 border-b border-line font-display text-ink">Online now</div>
                {onlineAdmins.map((admin) => (
                  <div key={admin.sessionId} className="px-4 py-3 border-b border-line last:border-0 flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full bg-ink/10 text-ink text-xs font-semibold flex items-center justify-center shrink-0">
                      {initials(admin.fullName || admin.username)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-ink font-medium truncate">{admin.fullName || admin.username}</p>
                      <p className="text-xs text-ink-faint">
                        <span className={admin.status === 'online' ? 'text-green-600' : 'text-yellow-600'}>
                          {admin.status === 'online' ? 'Online' : 'Away'}
                        </span>
                        {admin.currentPage && ` · on ${admin.currentPage}`}
                      </p>
                      <p className="text-xs text-ink-faint truncate">
                        {admin.device}{(admin.city || admin.country) && ` · ${[admin.city, admin.country].filter(Boolean).join(', ')}`}
                      </p>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PresenceBar;
